import React, { useEffect, useRef, useState } from 'react';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import { HEADER_HEIGHT } from 'src/lib/constants';
import { Author, Blog, SiteReview, Pagination as TPagination, User } from 'src/types';
import ApiClient from 'src/lib/api_client';
import { range, min } from 'lodash-es';
import RevalidateSettings from 'src/lib/revalidate_settings';
import BlogCardV2, { BlogCardSkeleton } from 'src/components/BlogCardV2';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';
import UserAvatar from 'src/components/account/UserAvatar';
import { FiChevronRight } from 'react-icons/fi';
import InstagramSvg from 'public/images/svg_icons/instagram.svg';
import LinkedInSvg from 'public/images/svg_icons/linkedin.svg';
import useQuery from 'src/hooks/useQuery';
import Loading from 'src/components/Loading';
import { useRouter } from 'next/router';
import Pagination from 'src/components/Pagination';
import { scrollTo } from 'src/helpers';
import Link from 'next/link';
import { NextSeo, OrganizationJsonLd } from 'next-seo';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import PersonJsonLd from 'src/components/jsonld/PersonJsonLd';

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  author,
  reqPage,
  siteReview,
}) => {
  const addLocaleToUrl = useAddLocaleToUrl();
  const router = useRouter();
  const translation = useTranslations('pages/magazine/author/[id]');
  const { page: pageQuery } = router.query;
  const [page, setPage] = useState(1);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const numSkeletons = min([6, 9]) as number;
  const blogsBodyRef = useRef<HTMLDivElement>(null);
  const fallbackData: { data: Blog[]; pagination: TPagination } = {
    data: [],
    pagination: {} as TPagination,
  };
  const { data, isLoading } = useQuery<{ data: Blog[]; pagination: TPagination }>(
    ApiRoutes.apiBlogsRoute(
      {},
      {
        user_id: author.id,
        sort: 'most_popular',
        page: page,
        per_page: 6,
      }
    ).toUrl()
  );

  const { data: fetchedBlogs, pagination } = data || fallbackData;

  useEffect(() => {
    if (page > 1) {
      if (!isLoading) {
        setBlogs((prev) => [...fetchedBlogs]);
      }
    } else {
      setBlogs(fetchedBlogs);
    }
  }, [fetchedBlogs.map((o) => o.id).join(','), isLoading]);

  useEffect(() => {
    if (pageQuery && Number(pageQuery)) {
      if (Number(pageQuery) !== page) {
        setPage(Number(pageQuery));
      }
    }
  }, [pageQuery]);

  return (
    <>
      <NextSeo
        title={author.name || ''}
        description={author.description || ''}
        canonical={addLocaleToUrl(
          AppRoutes.authorMagazineRoute(
            { id: author.slug },
            reqPage ? { page: reqPage } : {}
          ).toUrl()
        )}
        openGraph={{
          url: addLocaleToUrl(
            AppRoutes.authorMagazineRoute(
              { id: author.slug },
              reqPage ? { page: reqPage } : {}
            ).toUrl()
          ),
          title: author.name || '',
          description: author.description || '',
          images: author.avatar
            ? [
                {
                  url: author.avatarUrl || '',
                  height: 600,
                  width: 600,
                  alt: author.name || '',
                },
              ]
            : undefined,
        }}
      />
      <WithDefaultJsonLd>
        <OrganizationJsonLd
          name={author.name || ''}
          url={addLocaleToUrl(
            AppRoutes.authorMagazineRoute(
              { id: author.slug },
              reqPage ? { page: reqPage } : {}
            ).toUrl()
          )}
          image={author.avatarUrl}
          description={author.description}
          aggregateRating={{
            ratingValue: siteReview.rating,
            reviewCount: siteReview.count,
          }}
        />

        <PersonJsonLd
          name={author.name}
          email={author.email}
          url={addLocaleToUrl(
            AppRoutes.authorMagazineRoute(
              { id: author.slug },
              reqPage ? { page: reqPage } : {}
            ).toUrl()
          )}
          image={author.avatarUrl}
          sameAs={[author.instagramUrl, author.linkedInUrl].filter((o) => o !== undefined)}
          jobTitle="Author"
          description={author.description || ''}
          disambiguatingDescription={author.shortDescription || ''}
        />
      </WithDefaultJsonLd>
      <div className="tw-bg-brand-yellow md:tw-py-12 tw-py-6">
        <div className="container md:tw-max-w-xl">
          <div className="tw-flex tw-items-center tw-justify-center tw-mb-12">
            <span className="tw-font-semibold tw-text-sm tw-mr-1">{translation('fieldNotes')}</span>
            <FiChevronRight size={18} className="tw-text-brand-navy" strokeWidth={2} />
          </div>

          <div className="tw-flex md:tw-flex-row tw-flex-col tw-mb-8 tw-px-3 tw-items-center">
            <div className="md:tw-mb-0 tw-mb-4">
              <UserAvatar
                currentUser={author}
                className="tw-h-28 tw-w-28"
                borderClassName="tw-border-2 tw-border-brand-green tw-border-solid"
              />
            </div>
            <div className="md:tw-ml-8 tw-text-center md:tw-text-left">
              <h1 className="tw-text-3xl md:tw-text-2xl tw-font-semibold tw-font-styleSans">
                {author.name}
              </h1>
              <div>
                <div className="tw-py-2 tw-flex md:tw-justify-start tw-justify-center">
                  {author.linkedInUrl ? (
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      legacyBehavior
                      prefetch={false}
                      href={author.linkedInUrl}
                    >
                      <a target="_blank" className="tw-block tw-cursor-pointer tw-mr-2">
                        <LinkedInSvg
                          className="tw-text-brand-navy tw-align-top"
                          width={20}
                          height={20}
                        />
                      </a>
                    </Link>
                  ) : null}

                  {author.instagramUrl ? (
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      legacyBehavior
                      prefetch={false}
                      href={author.instagramUrl}
                    >
                      <a target="_blank" className="tw-block tw-cursor-pointer">
                        <InstagramSvg
                          className="tw-text-brand-navy tw-align-top"
                          width={20}
                          height={20}
                        />
                      </a>
                    </Link>
                  ) : null}
                </div>
                {author.shortDescription ? (
                  <div className="tw-text-grey-dark md:tw-max-w-md tw-mx-auto tw-text-left">
                    {author.shortDescription}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="tw-px-3">
            {author.bio ? (
              <>
                <h2 className="tw-font-semibold tw-text-base tw-mb-2">{translation('aboutMe')}</h2>
                <div className="tw-mb-8">{author.bio}</div>
              </>
            ) : null}
            {author.description ? (
              <>
                <h2 className="tw-font-semibold tw-text-base tw-mb-2">{translation('aLifeChanging')}</h2>
                <div className="tw-mb-8">{author.description}</div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {blogs.length > 0 ? (
        <div ref={blogsBodyRef} className="md:tw-py-24 tw-py-16">
          <div className="container">
            <div className="tw-text-center tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-mb-8">
              {translation('allFieldNotes', { name: author.name })}
            </div>

            <div className="row row-original">
              {blogs.map((blog) => (
                <div key={blog.id} className="col-md-4 tw-mb-8">
                  <BlogCardV2 blog={blog} />
                </div>
              ))}
            </div>

            {isLoading ? (
              <div className="tw-mt-10">
                <Loading className="tw-justify-center" />
              </div>
            ) : null}

            <div className="tw-flex tw-justify-center tw-mt-8">
              {pagination.totalPages > 1 ? (
                <Pagination
                  paginationData={pagination}
                  window={2}
                  onClick={(page) => {
                    router.push(
                      {
                        pathname: router.pathname,
                        query: { ...router.query, page },
                      },
                      undefined,
                      { shallow: true }
                    );

                    if (blogsBodyRef.current) {
                      scrollTo(blogsBodyRef.current, -HEADER_HEIGHT);
                    }
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : isLoading ? (
        numSkeletons > 0 ? (
          <div className="container tw-py-16">
            <div className="row row-original">
              {range(numSkeletons).map((i) => (
                <div key={i} className="col-md-4 tw-mb-8 md:tw-mb-0">
                  <BlogCardSkeleton />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="tw-mt-10">
            <Loading className="tw-justify-center" />
          </div>
        )
      ) : null}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{
  author: Author;
  reqPage: number | null;
  siteReview: SiteReview;
}> = async (context) => {
  if (!context.params?.id) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.medium,
    };
  }

  const translations = await prepareTranslations(context, [
    'pages/magazine/author/[id]',
    'pages/404',
  ]);

  const [author] = await ApiClient.getAuthor(
    { id: String(context.params.id) },
    { 'X-NEXT-LOCALE': translations.locale }
  );

  if ('error' in author) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.long,
    };
  }

  const [siteReview] = await ApiClient.getSiteReviews();

  return {
    props: {
      author: author,
      siteReview: siteReview,
      reqPage: context.params?.page ? Number(context.params.page) : null,
      ...translations,
    },
    revalidate: RevalidateSettings.short,
  };
};

export default Page;
